import { Grid2, Button, Stack, Typography, InputLabel, OutlinedInput, FormHelperText, Box } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useEffect, useRef } from 'react';
import RequiredIndicator from 'components/@extended/RequiredIndicator';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';
import { createSkillSector, updateSkillSector } from '../../services/skillSector.service';

const STORAGE_KEY = 'skill_sector_draft';

interface SkillSectorFormValues {
  name: string;
}

const AddSkillSector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.data || null;
  const isMounted = useRef(false);
  const getInitialValues = (): SkillSectorFormValues => {
    if (isEdit && editData) {
      return {
        name: editData.name || ''
      };
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const draft = JSON.parse(stored);
        if (draft?.userId === user?._id && draft.name?.trim()) {
          return {
            name: draft.name
          };
        }
      } catch (e) {
        console.warn('Invalid or corrupted skill sector draft in localStorage');
      }
    }

    return { name: '' };
  };

  const initialValues = getInitialValues();

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(3, 'Sector name must be at least 3 characters')
      .max(120, 'Sector name is too long')
      .required('Sector name is required')
  });

  const handleSubmit = async (values: SkillSectorFormValues, helpers: FormikHelpers<SkillSectorFormValues>) => {
    helpers.setSubmitting(true);

    const payload = {
      name: values.name.trim()
    };

    try {
      let res;
      if (isEdit && editData?._id) {
        res = await updateSkillSector(editData._id, payload);
        Snackbar('Skill Sector updated successfully', 'success');
      } else {
        res = await createSkillSector(payload);
        Snackbar('Skill Sector created successfully', 'success');
      }

      if (res?.status === 200 || res?.status === 201) {
        localStorage.removeItem(STORAGE_KEY);
        navigate(-1);
      } else {
        throw new Error(res?.data?.message || 'Unexpected response status');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save skill sector';
      Snackbar(errorMessage, 'error');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <MainCard
          content={false}
          title={
            <Stack direction="row" spacing={2} alignItems="center">
              <BackToggle show />
              <Stack>
                <Typography variant="h5">
                  <FormattedMessage id={isEdit ? 'Edit Skill Sector' : 'Add Skill Sector'} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isEdit && editData?.isAssignedToQP
                    ? 'This Skill Sector is assigned to one or more Qualification Packs and cannot be modified.'
                    : isEdit
                      ? 'Update skill sector details'
                      : 'Create a new skill sector'}
                </Typography>
              </Stack>
            </Stack>
          }
        >
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, dirty, isValid, isSubmitting }) => {
              const formDisabled = isEdit && Boolean(editData?.isAssignedToQP);
              useEffect(() => {
                if (isEdit) return;

                if (!isMounted.current) {
                  isMounted.current = true;
                  return;
                }

                if (dirty) {
                  const draftData = {
                    name: values.name,
                    userId: user?._id
                  };
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
                }
              }, [values.name, dirty, isEdit, user?._id]);

              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Box p={3}>
                    <Grid2 container spacing={2.5}>
                      <Grid2 size={12}>
                        <InputLabel>
                          Skill Sector Name <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(errors.name)}
                          placeholder="e.g. Pharmacy Sector Skill Council"
                          disabled={formDisabled}
                        />
                        <FormHelperText error>{touched.name && errors.name}</FormHelperText>
                      </Grid2>
                    </Grid2>

                    <Stack direction="row" justifyContent="flex-end" mt={4} spacing={2}>
                      <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} disabled={isSubmitting}>
                        Cancel
                      </Button>

                      {!formDisabled && (
                        <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || (isEdit && !dirty)}>
                          {isEdit ? 'Update Sector' : 'Create Sector'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </MainCard>
      </Grid2>
    </Grid2>
  );
};

export default AddSkillSector;
